<template>
  <div class="space-y-12">
    <div class="flex flex-row items-center justify-between">
      <span class="text-2xl font-medium sm:text-4xl">
        {{ __('Challenges') }}
      </span>
    </div>

    <EmptyState
        v-if="!challenges.length && !hasActiveFiltersOrSearch"
        :title="__('No Challenges Yet')"
        :subtitle="__('Start exploring challenges to boost your skills and earn points')"
        :button-text="__('Browse Challenges')"
        button-route="challenges.index"
   />
    <div v-else class="bg-white p-6 rounded-lg">
      <div class="flex w-full justify-between items-center mb-4">
        <div class="flex items-center gap-2">
          <button
              @click="showFilters = !showFilters"
              class="inline-flex items-center gap-2 text-primary hover:text-primary/70"
          >
            <Icon name="filter_alt" class="w-5 h-5 text-2xl" is-filled/>
            <span class="text-base font-medium">{{ __('Filter Challenges') }}</span>
          </button>
          <span v-if="activeFiltersCount > 0" class="text-sm text-gray-600">
            {{ activeFiltersCount }} {{ activeFiltersCount === 1 ? __('filter') : __('filters') }} {{ __('applied') }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <OrderChallengesPopup v-model="order" @order-changed="applyOrder" />
        </div>
      </div>
      <Transition name="slide-down">
        <div v-if="showFilters" class="bg-white rounded-lg border border-zinc-200 p-6 space-y-4 relative z-50 mb-6" style="overflow: visible !important;">
          <div class="relative grid md:grid-cols-2 grid-cols-1 items-center gap-6" style="overflow: visible !important;">
            <div class="col-span-1 relative" style="z-index: 101; overflow: visible !important;">
              <InputSelect
                  id="pointsFilter"
                  v-model:value="filters.category"
                  :label="__('Category')"
                  :placeholder="__('Filter by category')"
                  :options="categoryOptions"
              />
            </div>
            <div class="col-span-1">
              <InputSelect
                  id="pointsFilter"
                  v-model:value="filters.time_left"
                  :label="__('Time left')"
                  :placeholder="__('Filter by the time left')"
                  :options="timeLeftOptions"
              />
            </div>
            <div class="col-span-1 relative" style="z-index: 100; overflow: visible !important;">
              <InputSelect
                  id="pointsFilter"
                  v-model:value="filters.points"
                  :label="__('Points')"
                  :placeholder="__('Filter by the allocated number of points')"
                  :options="pointsOptions"
              />
            </div>
            <div class="col-span-1">
              <InputSelect
                  id="pointsFilter"
                  v-model:value="filters.company"
                  :label="__('Company')"
                  :placeholder="__('Filter by company')"
                  :options="companyOptions"
              />
            </div>
          </div>

          <div class="flex justify-center md:justify-end gap-2 md:gap-4 pt-4">
            <button
                @click="resetFilters"
                class="flex items-center gap-2 text-primary px-4 hover:text-primary/80"
            >
              <Icon name="delete" class="w-4 h-4 text-xl"/>
              <span class="text-sm">{{ __('Reset Filters') }}</span>
            </button>
            <Button
                @click="applyFilters"
                class="bg-primary text-white "
            >
              <span class="text-sm">{{ __('Apply Filters') }}</span>
            </Button>
          </div>
        </div>
      </Transition>
      <div class="relative mb-6 w-full">
        <input
            id="submission-search"
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :placeholder="__('Search')"
            class="w-full bg-white rounded-lg border border-gray-300 px-4 py-2.5 pl-12 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-base"
            @input="handleSearch"
            autocomplete="off"
        />

        <span class="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="6"></circle>
            <path d="M21 21l-4.35-4.35"></path>
          </svg>
        </span>
      </div>
      <div v-if="!challenges.length && hasActiveFiltersOrSearch" class="flex flex-col items-center justify-start pt-20 h-96">
         <span class="text-zinc-800 font-medium text-xl pb-4">{{ __('No challenges found')}}</span>
         <span class="text-zinc-800 text-base">{{ __(`We couldn't find any challenges that match your search`)}}</span>
         <span class="text-zinc-800 text-base">{{ __('Try adjusting your filters or searching for a different keyword')}}</span>
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
        <CandidateChallengeCard
            v-for="ch in challenges"
            :key="ch.id"
            :challenge="ch"
            @save="handleSaveChallenge"
            @unsave="handleUnsaveChallenge"
        />
      </div>
    </div>

    <div v-if="lastPage > 1" class="my-6">
      <Pagination
          :current-page="currentPage"
          :last-page="lastPage"
          @update:currentPage="handlePageChange"
      />
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from 'vue'
import CandidateChallengeCard from "@/pages/candidate/challenges/components/CandidateChallengeCard.vue";
import {useToast} from "vue-toastification";
import axios from "axios";
import EmptyState from "@/pages/candidate/challenges/components/EmptyState.vue";
import {__} from "@/helpers";
import { Pagination} from "@/components/global";
import {Icon} from "@/components/block/icon";
import {Button} from "@/components/block/button";
import {InputSelect} from "@/components/form/input-select";
import OrderChallengesPopup from "@/pages/company/challenges/components/OrderChallengesPopup.vue";

const props = defineProps({
  data: {
    type: Object,
    required: true
  }
})
console.log(props.data)
const toast = useToast();

const challengesData = ref(props.data.challenges || []);
const paginationData = ref(props.data.pagination || {});
const filterOptions = ref(props.data.filter_options || {});

const challenges = computed(() => {
  return Array.isArray(challengesData.value) ? challengesData.value : [];
});

const currentPage = ref(paginationData.value.current_page || 1);
const lastPage = computed(() => paginationData.value.last_page || 1);

const searchQuery = ref(props.data.search || '');
const searchTimeout = ref(null);

const loading = ref(false);

const showFilters = ref(false);

const filters = ref({
  category: props.data.filters?.category || null,
  time_left: props.data.filters?.time_left || null,
  points: props.data.filters?.points || null,
  company: props.data.filters?.company || null,
});

const order = ref(props.data.order || 'newest');

const categoryOptions = computed(() => filterOptions.value.categories || []);
const companyOptions = computed(() => filterOptions.value.companies || []);
const pointsOptions = computed(() => filterOptions.value.points || []);
const timeLeftOptions = computed(() => filterOptions.value.time_left || []);

const activeFiltersCount = computed(() => {
  let count = 0;
  if (filters.value.category) count++;
  if (filters.value.company) count++;
  if (filters.value.points) count++;
  if (filters.value.time_left) count++;
  return count;
});

const hasActiveFiltersOrSearch = computed(() => {
  return searchQuery.value.length > 0 || activeFiltersCount.value > 0;
})

const handleSaveChallenge = async (challenge) => {
  try {
    await axios.post(route('redesign.candidate.challenges.save', {
      challenge: challenge.id
    }));
    toast.success(__('Challenge saved successfully'));
  } catch (error) {
    toast.error(__('Failed to save challenge'));
  }
}

const handleUnsaveChallenge = async (challenge) => {
  try {
    await axios.delete(route('redesign.candidate.challenges.unsave', {
      challenge: challenge.id
    }));
    toast.success(__('Challenge removed from saved list'));
  } catch (error) {
    toast.error(__('Failed to remove challenge'));
  }
}

const handleSearch = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  searchTimeout.value = setTimeout(() => {
    currentPage.value = 1;
    fetchChallenges();
  }, 300);
}

const handlePageChange = (page) => {
  currentPage.value = page;
  fetchChallenges();
}

const applyFilters = async () => {
  showFilters.value = false;
  await fetchChallenges();
  showFilters.value = false;
};

const resetFilters = async () => {
  filters.value = {
    points: null,
    category: null,
    time_left: null,
    company: null,
  };
  searchQuery.value = '';
  currentPage.value = 1;
  await fetchChallenges();
};

const applyOrder = async (value) => {
  order.value = value;
  currentPage.value = 1;
  await fetchChallenges();
}

const fetchChallenges = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      search: searchQuery.value,
      category: filters.value.category?.id || filters.value.category,
      time_left: filters.value.time_left?.id || filters.value.time_left,
      company: filters.value.company?.id || filters.value.company,
      points: filters.value.points?.id || filters.value.points,
      order: order.value,
    };

    const response = await axios.get(route('challenges.index'), { params });

    if (response.data && response.data.data) {
      challengesData.value = response.data.data.challenges || [];
      paginationData.value = response.data.data.pagination || [];
      filterOptions.value = response.data.data.filter_options || {};
    }
  } catch (error) {
    toast.error(__('Error fetching challenges.'));
  } finally {
    loading.value = false;
  }
}
</script>
